import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Input, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { configAPI } from '../services/api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

function ConfigManagement() {
  const [loading, setLoading] = useState(false);
  const [savingLoginAccess, setSavingLoginAccess] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [loginAccess, setLoginAccess] = useState({
    allowAll: true,
    whitelistText: '',
    whitelistAccounts: [],
    whitelistCount: 0
  });

  useEffect(() => {
    loadConfigs();
    loadLoginAccess();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await configAPI.getSystem();
      setConfigs(response.data.data);
    } catch (error) {
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginAccess = async () => {
    try {
      const response = await configAPI.getLoginAccess();
      setLoginAccess(response.data.data);
    } catch (error) {
      message.error('加载登录配置失败');
    }
  };

  const saveLoginAccess = async () => {
    setSavingLoginAccess(true);
    try {
      const response = await configAPI.updateLoginAccess({
        allowAll: loginAccess.allowAll,
        whitelistText: loginAccess.whitelistText
      });
      setLoginAccess(response.data.data);
      message.success('登录配置保存成功');
      loadConfigs();
    } catch (error) {
      message.error('保存登录配置失败');
    } finally {
      setSavingLoginAccess(false);
    }
  };

  const columns = [
    {
      title: '配置名称',
      dataIndex: 'name',
      key: 'name',
      render: (value) => renderTableStack(value || '未命名配置', '系统配置项')
    },
    {
      title: '长整型值',
      dataIndex: 'long_value1',
      key: 'long_value1',
      render: (value) => renderTableStack(value ?? '-', 'long_value1')
    },
    {
      title: '整型值',
      dataIndex: 'int_value1',
      key: 'int_value1',
      render: (value) => renderTableStack(value ?? '-', 'int_value1')
    },
    {
      title: '字符串值',
      dataIndex: 'string_value1',
      key: 'string_value1',
      render: (value) => renderTableStack(value || '-', 'string_value1')
    },
    {
      title: '文本值',
      dataIndex: 'text_value1',
      key: 'text_value1',
      ellipsis: true,
      render: (value) => renderTableStack(value || '-', 'text_value1')
    },
    {
      title: '操作',
      key: 'action',
      render: () => (
        <Button type="link" icon={<EditOutlined />}>
          编辑
        </Button>
      )
    }
  ];

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">配置管理</div>
          <div className="page-subtitle">集中维护系统配置和登录控制，避免再在多个页面之间来回切换。</div>
        </div>
        <div className="page-header-actions">
          <Tag color="blue">系统配置</Tag>
          <Tag color={loginAccess.allowAll ? 'green' : 'orange'}>{loginAccess.allowAll ? '全部可登录' : '白名单模式'}</Tag>
        </div>
      </div>

      <div className="page-note">
        <div className="page-note-text">
          <span className="page-note-strong">这页优先展示最常改的登录控制。</span>
          白名单内容和底层系统配置仍在同一工作区，便于同时核对。
        </div>
        <Text type="secondary">当前白名单 {loginAccess.whitelistCount || 0} 个</Text>
      </div>

      <Card title="游戏登录控制" className="section-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="开启时所有账号都可以登录；关闭时只有白名单中的账号可以登录游戏。"
          />
          <div>
            <Text strong style={{ marginRight: 12 }}>登录开关</Text>
            <Switch
              checked={loginAccess.allowAll}
              checkedChildren="全部可登录"
              unCheckedChildren="仅白名单"
              onChange={(checked) => setLoginAccess((prev) => ({ ...prev, allowAll: checked }))}
            />
          </div>
          <div>
            <Text strong>白名单设置</Text>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              支持按换行、英文逗号、中文逗号、分号分隔账号。可填写登录账号或手机号。
            </Paragraph>
            <TextArea
              rows={8}
              value={loginAccess.whitelistText}
              onChange={(event) => setLoginAccess((prev) => ({ ...prev, whitelistText: event.target.value }))}
              placeholder={'示例:\nadmin001\n18800001111\nplayer_test'}
            />
          </div>
          <div>
            <Text strong style={{ marginRight: 8 }}>当前白名单数量</Text>
            <Tag color={loginAccess.allowAll ? 'blue' : 'orange'}>{loginAccess.whitelistCount || 0}</Tag>
          </div>
          {Array.isArray(loginAccess.whitelistAccounts) && loginAccess.whitelistAccounts.length > 0 && (
            <div>
              <Text strong>白名单预览</Text>
              <div className="table-tag-row" style={{ marginTop: 12 }}>
                {loginAccess.whitelistAccounts.map((account) => (
                  <Tag key={account}>
                    {account}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          <div>
            <Button type="primary" onClick={saveLoginAccess} loading={savingLoginAccess}>
              保存登录配置
            </Button>
          </div>
        </Space>
      </Card>

      <Card className="table-card" title="系统配置列表" extra={<Text type="secondary">共 {configs.length} 项配置</Text>}>
        <Table
          columns={columns}
          dataSource={configs}
          rowKey="name"
          loading={loading}
          pagination={false}
          locale={{ emptyText: renderEmptyPanel('暂无系统配置数据', '当前没有返回可展示的系统配置项。') }}
        />
      </Card>
    </div>
  );
}

export default ConfigManagement;
